import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditCard, Plus, Search, Trash2 } from 'lucide-react'
import { adminAPI, licenseAPI } from '../../services/api'
import type { License } from '../../types'
import toast from 'react-hot-toast'

const LicenseManagement: React.FC = () => {
  const { t } = useTranslation()
  const [licenses, setLicenses] = useState<License[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filters, setFilters] = useState({
    status: '',
    user_email: ''
  })
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newLicense, setNewLicense] = useState({
    userEmail: '',
    planId: '6',
    durationDays: '30'
  })

  useEffect(() => {
    fetchLicenses()
  }, [filters])

  const fetchLicenses = async () => {
    try {
      const response = await adminAPI.getLicenses(filters)
      console.log('Admin licenses response:', response.data)
      
      // Handle different response formats
      const responseData = response.data as any
      if (responseData && responseData.success && responseData.data) {
        // API zwraca {success: true, data: [...]}
        setLicenses(responseData.data)
      } else if (Array.isArray(responseData)) {
        setLicenses(responseData)
      } else if (responseData && responseData.licenses) {
        setLicenses(responseData.licenses)
      } else {
        console.error('Unexpected licenses response format:', responseData)
        setLicenses([])
      }
    } catch (error) {
      console.error('Error fetching licenses:', error)
      toast.error('Błąd pobierania licencji')
      setLicenses([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateLicense = async () => {
    if (!newLicense.userEmail) {
      toast.error('Podaj email użytkownika')
      return
    }
    
    try {
      // Najpierw znajdź użytkownika po emailu
      const usersResponse = await adminAPI.getUsers()
      const responseData = usersResponse.data as any
      const userData = responseData?.success ? responseData.data : responseData
      const user = userData.find((u: any) => u.email === newLicense.userEmail)
      
      if (!user) {
        toast.error('Użytkownik o podanym emailu nie istnieje')
        return
      }
      
      await licenseAPI.createLicense({
        userId: user.id,
        planId: parseInt(newLicense.planId),
        durationDays: parseInt(newLicense.durationDays)
      })
      toast.success('Licencja utworzona')
      setShowCreateForm(false)
      setNewLicense({ userEmail: '', planId: '6', durationDays: '30' })
      fetchLicenses()
    } catch (error: any) {
      console.error('Create license error:', error)
      toast.error(error.response?.data?.error || 'Błąd tworzenia licencji')
    }
  }

  const handleDeleteLicense = async (licenseId: number) => {
    if (!confirm('Czy na pewno chcesz usunąć tę licencję? Ta akcja jest nieodwracalna.')) return

    try {
      await adminAPI.deleteLicense(licenseId)
      toast.success('Licencja została usunięta')
      fetchLicenses()
    } catch (error: any) {
      console.error('Delete license error:', error)
      toast.error(error.response?.data?.message || 'Błąd usuwania licencji')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-900/20 text-green-400 border-green-900/50'
      case 'trial':
        return 'bg-blue-900/20 text-blue-400 border-blue-900/50'
      case 'suspended':
        return 'bg-yellow-900/20 text-yellow-400 border-yellow-900/50'
      case 'expired':
        return 'bg-gray-900/20 text-gray-400 border-gray-900/50'
      case 'cancelled':
        return 'bg-red-900/20 text-red-400 border-red-900/50'
      default:
        return 'bg-gray-900/20 text-gray-400 border-gray-900/50'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktywna'
      case 'trial':
        return 'Próbna'
      case 'suspended':
        return 'Zawieszona'
      case 'expired':
        return 'Wygasła'
      case 'cancelled':
        return 'Anulowana'
      default:
        return status
    }
  }

  const handleStatusChange = async (licenseId: number, newStatus: string) => {
    try {
      await adminAPI.updateLicenseStatus(licenseId, newStatus)
      toast.success('Status licencji został zmieniony')
      fetchLicenses()
    } catch (error: any) {
      console.error('Update status error:', error)
      toast.error(error.response?.data?.message || 'Błąd zmiany statusu')
    }
  }

  const handleExtendLicense = async (licenseId: number, days: number) => {
    if (days === 0) return // Nie rób nic jeśli wybrano placeholder
    
    try {
      await adminAPI.extendLicense(licenseId, days)
      toast.success(`Licencja przedłużona o ${days} dni`)
      fetchLicenses()
    } catch (error: any) {
      console.error('Extend license error:', error)
      toast.error(error.response?.data?.message || 'Błąd przedłużania licencji')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('admin.licenses')}</h1>
          <p className="text-gray-400">Zarządzaj licencjami użytkowników</p>
        </div>
        <button 
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
          <Plus className="h-4 w-4" />
          <span>Utwórz licencję</span>
        </button>
      </div>

      {/* Create License Form */}
      {showCreateForm && (
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <h3 className="text-lg font-semibold text-white mb-4">Utwórz nową licencję</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email użytkownika</label>
              <input
                type="email"
                value={newLicense.userEmail}
                onChange={(e) => setNewLicense(prev => ({ ...prev, userEmail: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Plan</label>
              <select
                value={newLicense.planId}
                onChange={(e) => setNewLicense(prev => ({ ...prev, planId: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
              >
                <option value="6">Standard - 5 urządzeń (24.99 EUR/rok)</option>
                <option value="7">Pro - 50 urządzeń (59.99 EUR/rok)</option>
                <option value="8">Unlimited - bez limitu (599.00 EUR - dożywotnio)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Czas trwania (dni)</label>
              <input
                type="number"
                value={newLicense.durationDays}
                onChange={(e) => setNewLicense(prev => ({ ...prev, durationDays: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
              />
            </div>
          </div>
          <div className="mt-4 flex space-x-2">
            <button
              onClick={handleCreateLicense}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
            >
              Utwórz
            </button>
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-dark-800 rounded-lg p-4 border border-dark-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Wszystkie</option>
              <option value="active">Aktywne</option>
              <option value="trial">Próbne</option>
              <option value="suspended">Zawieszone</option>
              <option value="expired">Wygasłe</option>
              <option value="cancelled">Anulowane</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Email użytkownika</label>
            <div className="relative">
              <input
                type="text"
                value={filters.user_email}
                onChange={(e) => setFilters(prev => ({ ...prev, user_email: e.target.value }))}
                placeholder="Wyszukaj po emailu..."
                className="w-full px-3 py-2 pl-10 bg-dark-700 border border-dark-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <CreditCard className="h-5 w-5 text-primary mr-2" />
          Lista licencji
        </h2>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse bg-dark-700 h-16 rounded"></div>
            ))}
          </div>
        ) : licenses.length === 0 ? (
          <p className="text-center text-gray-400 py-8">Brak licencji do wyświetlenia</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Klucz licencji</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Użytkownik</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Plan</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Wygasa</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Urządzenia</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Przedłuż aktywację</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Zmień status</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((license) => (
                  <tr key={license.id} className="border-b border-dark-700 hover:bg-dark-700/50">
                    <td className="py-4 px-4">
                      <p className="font-mono text-sm text-gray-300">{license.license_key}</p>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="text-white">{license.user_name || `User #${license.user_id}`}</p>
                        {license.user_email && (
                          <p className="text-sm text-gray-400">{license.user_email}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-white">{license.plan_name}</p>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(license.status)}`}>
                        {getStatusLabel(license.status)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-300">
                        {new Date(license.valid_to).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-300">
                        {license.active_devices || 0} / {license.max_devices}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <select
                        defaultValue="0"
                        onChange={(e) => handleExtendLicense(license.id, parseInt(e.target.value))}
                        className="px-3 py-1 bg-dark-700 border border-dark-500 rounded-md text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="0">Wybierz okres</option>
                        <option value="30">Miesiąc</option>
                        <option value="365">Rok</option>
                        <option value="36500">Unlimited (dożywotnio)</option>
                      </select>
                    </td>
                    <td className="py-4 px-4">
                      <select
                        value={license.status}
                        onChange={(e) => handleStatusChange(license.id, e.target.value)}
                        className="px-3 py-1 bg-dark-700 border border-dark-500 rounded-md text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="active">Aktywna</option>
                        <option value="suspended">Zawieszona</option>
                        <option value="cancelled">Anulowana</option>
                      </select>
                    </td>
                    <td className="py-4 px-4">
                      <button
                        onClick={() => handleDeleteLicense(license.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-600/10 rounded-lg transition-colors"
                        title="Usuń licencję"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default LicenseManagement